import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { appointmentAPI } from '../../lib/api';
import { 
  VideoCamera, 
  Microphone, 
  MicrophoneSlash,
  Camera,
  CameraSlash,
  Phone,
  PhoneDisconnect,
  Gear,
  Users,
  Clock,
  Warning,
  CheckCircle,
  ArrowLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function VideoCallPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callStatus, setCallStatus] = useState('waiting'); // waiting, connecting, connected, ended
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [error, setError] = useState(null);
  const localVideoRef = useRef(null);

  useEffect(() => {
    loadAppointment();
  }, [appointmentId]);

  const loadAppointment = async () => {
    try {
      const response = await appointmentAPI.getAll();
      const apt = response.data.find(a => a.id === appointmentId);
      if (apt) {
        setAppointment(apt);
        // Check if appointment time is now
        const aptTime = new Date(apt.scheduled_time);
        const now = new Date();
        const timeDiff = (aptTime - now) / (1000 * 60); // difference in minutes
        
        if (timeDiff > 15) {
          setCallStatus('early');
        } else if (timeDiff < -60) {
          setCallStatus('expired');
        } else {
          setCallStatus('waiting');
        }
      } else {
        setError('Appointment not found');
      }
    } catch (err) {
      setError('Failed to load appointment');
    } finally {
      setLoading(false);
    }
  };

  const startLocalVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      toast.error('Unable to access camera/microphone');
      return null;
    }
  };

  const joinCall = async () => {
    setCallStatus('connecting');
    const stream = await startLocalVideo();
    if (stream) {
      // In production with Daily.co API key, this would join the actual room
      // For now, show the local preview
      setTimeout(() => {
        setCallStatus('connected');
        toast.success('Connected to video call');
      }, 1500);
    } else {
      setCallStatus('waiting');
    }
  };

  const endCall = () => {
    if (localVideoRef.current?.srcObject) {
      localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setCallStatus('ended');
    toast.info('Call ended');
  };

  const toggleMute = () => {
    if (localVideoRef.current?.srcObject) {
      const audioTracks = localVideoRef.current.srcObject.getAudioTracks();
      audioTracks.forEach(track => track.enabled = isMuted);
      setIsMuted(!isMuted);
    }
  };

  const toggleCamera = () => {
    if (localVideoRef.current?.srcObject) {
      const videoTracks = localVideoRef.current.srcObject.getVideoTracks();
      videoTracks.forEach(track => track.enabled = isCameraOff);
      setIsCameraOff(!isCameraOff);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-white">Loading appointment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center">
          <Warning size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Join Call</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="btn-teal"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="video-call-page">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="text-center">
            <h1 className="text-white font-semibold">Virtual Appointment</h1>
            {appointment && (
              <p className="text-slate-400 text-sm">
                {appointment.appointment_type?.replace(/_/g, ' ')} - {new Date(appointment.scheduled_time).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {callStatus === 'connected' && (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                Connected
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        {callStatus === 'early' && (
          <div className="bg-white rounded-2xl p-8 max-w-md text-center">
            <Clock size={48} className="text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Your Appointment Hasn&apos;t Started Yet</h2>
            <p className="text-slate-600 mb-2">
              Scheduled for: {new Date(appointment?.scheduled_time).toLocaleString()}
            </p>
            <p className="text-slate-500 text-sm mb-6">
              You can join up to 15 minutes before your appointment time.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="btn-outline"
            >
              Return to Appointments
            </button>
          </div>
        )}

        {callStatus === 'expired' && (
          <div className="bg-white rounded-2xl p-8 max-w-md text-center">
            <Warning size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Appointment Time Passed</h2>
            <p className="text-slate-600 mb-6">
              This appointment was scheduled for {new Date(appointment?.scheduled_time).toLocaleString()}. 
              Please reschedule if you missed your appointment.
            </p>
            <button
              onClick={() => navigate('/patient/appointments')}
              className="btn-teal"
            >
              View Appointments
            </button>
          </div>
        )}

        {callStatus === 'waiting' && (
          <div className="bg-white rounded-2xl p-8 max-w-md text-center">
            <VideoCamera size={48} className="text-teal-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Ready to Join?</h2>
            <p className="text-slate-600 mb-2">
              {appointment?.patient_name}&apos;s appointment with the provider
            </p>
            <p className="text-slate-500 text-sm mb-6">
              Make sure your camera and microphone are ready.
            </p>
            <button
              onClick={joinCall}
              className="btn-teal flex items-center gap-2 mx-auto"
              data-testid="join-call-btn"
            >
              <VideoCamera size={20} />
              Join Video Call
            </button>
          </div>
        )}

        {callStatus === 'connecting' && (
          <div className="text-center">
            <div className="spinner mx-auto mb-4" style={{ borderColor: '#14b8a6', borderTopColor: 'transparent' }} />
            <p className="text-white text-lg">Connecting to call...</p>
          </div>
        )}

        {callStatus === 'connected' && (
          <div className="w-full max-w-4xl">
            {/* Video Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Local Video */}
              <div className="relative bg-slate-800 rounded-xl overflow-hidden aspect-video">
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className={`w-full h-full object-cover ${isCameraOff ? 'hidden' : ''}`}
                />
                {isCameraOff && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center">
                      <CameraSlash size={32} className="text-slate-400" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-sm">
                  You {isMuted && '(Muted)'}
                </div>
              </div>

              {/* Remote Video Placeholder */}
              <div className="relative bg-slate-800 rounded-xl overflow-hidden aspect-video flex items-center justify-center">
                <div className="text-center">
                  <Users size={48} className="text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">Waiting for provider...</p>
                </div>
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-sm">
                  Provider
                </div>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-900/50 border border-blue-700 rounded-lg p-3 mb-4">
              <p className="text-blue-200 text-sm text-center">
                <strong>Note:</strong> Video conferencing requires a Daily.co API key to be configured. 
                Currently showing local camera preview only.
              </p>
            </div>
          </div>
        )}

        {callStatus === 'ended' && (
          <div className="bg-white rounded-2xl p-8 max-w-md text-center">
            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Call Ended</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your visit. Your provider may follow up with additional instructions.
            </p>
            <button
              onClick={() => navigate('/patient/appointments')}
              className="btn-teal"
            >
              Return to Appointments
            </button>
          </div>
        )}
      </div>

      {/* Controls */}
      {callStatus === 'connected' && (
        <div className="bg-slate-800 border-t border-slate-700 p-4">
          <div className="max-w-md mx-auto flex items-center justify-center gap-4">
            <button
              onClick={toggleMute}
              className={`p-4 rounded-full ${isMuted ? 'bg-red-500' : 'bg-slate-700 hover:bg-slate-600'}`}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <MicrophoneSlash size={24} className="text-white" />
              ) : (
                <Microphone size={24} className="text-white" />
              )}
            </button>

            <button
              onClick={toggleCamera}
              className={`p-4 rounded-full ${isCameraOff ? 'bg-red-500' : 'bg-slate-700 hover:bg-slate-600'}`}
              title={isCameraOff ? 'Turn camera on' : 'Turn camera off'}
            >
              {isCameraOff ? (
                <CameraSlash size={24} className="text-white" />
              ) : (
                <Camera size={24} className="text-white" />
              )}
            </button>

            <button
              onClick={endCall}
              className="p-4 rounded-full bg-red-500 hover:bg-red-600"
              title="End call"
              data-testid="end-call-btn"
            >
              <PhoneDisconnect size={24} className="text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
